import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Map,
  APIProvider,
  useMap,
} from "@vis.gl/react-google-maps";
import {
  MarkerClusterer,
  SuperClusterViewportAlgorithm,
  type Renderer,
  type Cluster,
  type ClusterStats,
} from "@googlemaps/markerclusterer";
import DashboardPage from "../../layout";
import Loader from "../../components/Loader";
import { getDeliveryCoordinates } from "../../api/delivery-driver";
import { getGlobalFilter } from "../../api/filters";
import {
  formatCurrentStartDate,
  formatCurrentEndDate,
} from "../../utility";
import { delivery_man } from "../../utility/constant";
import TimeFrameSelector from "../../components/TimeFrameSelector";
import DropdownMulti from "../../components/DropdownMultiple";
import Dropdown from "../../components/Dropdown";
import Button from "../../components/Button";
import Filter from "../../assets/icons/Filter.svg";
import Close from "../../assets/close-circle.svg";
import { useTranslation } from "react-i18next";

interface DeliveryPoint {
  lat: number;
  lng: number;
  status: string;
}

interface WeightedPoint {
  lat: number;
  lng: number;
  weight: number;
}

const GRID_PRECISION = 3;

const clusterPoints = (points: { lat: number; lng: number }[]): WeightedPoint[] => {
  const grid: Record<string, { lat: number; lng: number; count: number }> = {};
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const key = `${p.lat.toFixed(GRID_PRECISION)},${p.lng.toFixed(GRID_PRECISION)}`;
    const existing = grid[key];
    if (existing) {
      existing.lat += p.lat;
      existing.lng += p.lng;
      existing.count++;
    } else {
      grid[key] = { lat: p.lat, lng: p.lng, count: 1 };
    }
  }
  const result: WeightedPoint[] = [];
  const keys = Object.keys(grid);
  for (let i = 0; i < keys.length; i++) {
    const v = grid[keys[i]];
    result.push({ lat: v.lat / v.count, lng: v.lng / v.count, weight: v.count });
  }
  return result;
};

const STATUS_COLORS: Record<string, { label: string; gradient: string[]; dot: string }> = {
  DELIVERED: {
    label: "Delivered",
    gradient: [
      "rgba(72, 199, 142, 0)",
      "rgba(72, 199, 142, 0.3)",
      "rgba(52, 211, 153, 0.5)",
      "rgba(34, 197, 94, 0.65)",
      "rgba(22, 163, 74, 0.8)",
      "rgba(22, 163, 74, 1)",
    ],
    dot: "#22c55e",
  },
  CANCELLED: {
    label: "Cancelled",
    gradient: [
      "rgba(248, 113, 113, 0)",
      "rgba(248, 113, 113, 0.3)",
      "rgba(239, 68, 68, 0.5)",
      "rgba(220, 38, 38, 0.65)",
      "rgba(185, 28, 28, 0.8)",
      "rgba(185, 28, 28, 1)",
    ],
    dot: "#ef4444",
  },
  PENDING: {
    label: "Pending",
    gradient: [
      "rgba(253, 224, 71, 0)",
      "rgba(253, 224, 71, 0.3)",
      "rgba(250, 204, 21, 0.5)",
      "rgba(234, 179, 8, 0.65)",
      "rgba(202, 138, 4, 0.8)",
      "rgba(202, 138, 4, 1)",
    ],
    dot: "#eab308",
  },
};

const STATUSES = Object.keys(STATUS_COLORS) as Array<keyof typeof STATUS_COLORS>;

/**
 * Cluster bubbles keep a consistent status color independent of cluster density.
 */
function createDeliveryClusterRenderer(baseHex: string, labelColor: string): Renderer {
  return {
    render(cluster: Cluster, stats: ClusterStats, _map: google.maps.Map) {
      const count = cluster.count;
      const position = cluster.position;
      const _ = stats;
      const fillHex = baseHex;
      const o1 = 0.22;
      const o2 = 0.35;
      const o3 = 0.5;
      const o4 = 0.68;

      const size = Math.round(42 + Math.min(22, Math.log2(count + 1) * 6.5));

      const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="240" height="240">
        <circle cx="120" cy="120" fill="${fillHex}" opacity="${o1}" r="130"/>
        <circle cx="120" cy="120" fill="${fillHex}" opacity="${o2}" r="110"/>
        <circle cx="120" cy="120" fill="${fillHex}" opacity="${o3}" r="90"/>
        <circle cx="120" cy="120" fill="${fillHex}" opacity="${o4}" r="72"/>
      </svg>`;
      const url = `data:image/svg+xml;base64,${btoa(svg)}`;

      return new google.maps.Marker({
        position,
        icon: { url, scaledSize: new google.maps.Size(size, size) },
        label: {
          text: String(count),
          color: labelColor,
          fontSize: size > 50 ? "13px" : "12px",
          fontWeight: "700",
        },
        zIndex: Math.min(5000, 1000 + count),
      });
    },
  };
}

const saudiCenter = { lat: 23.8859, lng: 45.0792 };

/**
 * Many deliveries share identical or rounded GPS coordinates; stacked markers look like one dot.
 * Group by ~1m precision, then place each group on a small circle so every point is visible.
 */
const COORD_GROUP_DECIMALS = 5;

const spreadOverlappingCoordinates = (points: DeliveryPoint[]): DeliveryPoint[] => {
  if (points.length <= 1) return points;

  const groups: Record<string, DeliveryPoint[]> = {};
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const key = `${p.lat.toFixed(COORD_GROUP_DECIMALS)},${p.lng.toFixed(COORD_GROUP_DECIMALS)}`;
    const list = groups[key];
    if (list) list.push(p);
    else groups[key] = [p];
  }

  const out: DeliveryPoint[] = [];
  const groupKeys = Object.keys(groups);
  for (let g = 0; g < groupKeys.length; g++) {
    const group = groups[groupKeys[g]];
    if (group.length === 1) {
      out.push(group[0]);
      continue;
    }
    const centerLat = group[0].lat;
    const centerLng = group[0].lng;
    const n = group.length;
    const cosLat = Math.cos((centerLat * Math.PI) / 180);
    const safeCos = Math.abs(cosLat) < 1e-6 ? 1e-6 : cosLat;
    const radiusLat = 0.000035 * Math.sqrt(n);
    for (let i = 0; i < n; i++) {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2;
      const dLat = radiusLat * Math.cos(angle);
      const dLng = (radiusLat * Math.sin(angle)) / safeCos;
      out.push({
        ...group[i],
        lat: centerLat + dLat,
        lng: centerLng + dLng,
      });
    }
  }
  return out;
};

const StatusHeatmapLayer: React.FC<{
  clustered: WeightedPoint[];
  gradient: string[];
  visible: boolean;
}> = ({ clustered, gradient, visible }) => {
  const map = useMap();
  const heatmapRef = useRef<google.maps.visualization.HeatmapLayer | null>(null);

  useEffect(() => {
    if (!map) return;

    const init = () => {
      const g = (window as any).google;
      if (!g?.maps?.visualization?.HeatmapLayer) {
        setTimeout(init, 200);
        return;
      }

      if (!visible || clustered.length === 0) {
        if (heatmapRef.current) {
          heatmapRef.current.setMap(null);
          heatmapRef.current = null;
        }
        return;
      }

      const data = clustered.map((p) => ({
        location: new g.maps.LatLng(p.lat, p.lng),
        weight: p.weight,
      }));

      if (heatmapRef.current) {
        heatmapRef.current.setData(data);
        heatmapRef.current.setMap(map);
      } else {
        heatmapRef.current = new g.maps.visualization.HeatmapLayer({
          data,
          map,
          radius: 30,
          opacity: 0.65,
          maxIntensity: 15,
          gradient,
        });
      }
    };

    init();

    return () => {
      if (heatmapRef.current) {
        heatmapRef.current.setMap(null);
        heatmapRef.current = null;
      }
    };
  }, [map, clustered, gradient, visible]);

  return null;
};

/** Markers per coordinate; viewport clustering + custom colored clusters. */
const MARKER_BUILD_CHUNK = 900;

const CLUSTER_LABEL_LIGHT = "rgba(255,255,255,0.96)";
const CLUSTER_LABEL_DARK = "rgba(24,24,27,0.94)";

const DeliveryMarkersLayer: React.FC<{
  points: DeliveryPoint[];
  statusKey: keyof typeof STATUS_COLORS;
  visible: boolean;
}> = ({ points, statusKey, visible }) => {
  const fillColor = STATUS_COLORS[statusKey].dot;
  const clusterRenderer = useMemo(
    () =>
      createDeliveryClusterRenderer(
        fillColor,
        statusKey === "PENDING" ? CLUSTER_LABEL_DARK : CLUSTER_LABEL_LIGHT
      ),
    [fillColor, statusKey]
  );
  const map = useMap();
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!map) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      const clusterer = clustererRef.current;
      clustererRef.current = null;
      const markers = markersRef.current;
      markersRef.current = [];
      // Do not call clusterer.clearMarkers() — it runs render() and SuperClusterViewportAlgorithm
      // needs MapCanvasProjection; during unmount/teardown overlay projection can be gone while
      // map.getProjection() is still truthy. Detach markers, then remove overlay (onRemove → reset).
      markers.forEach((m) => {
        m.setMap(null);
      });
      if (clusterer) {
        clusterer.setMap(null);
      }
    };

    const build = () => {
      const g = window.google?.maps;
      if (!g?.Marker) {
        timeoutId = setTimeout(build, 150);
        return;
      }
      if (cancelled) return;

      cleanup();
      if (!visible || points.length === 0) return;

      // MarkerClusterer needs MapCanvasProjection; avoid fromLatLngToDivPixel on undefined
      if (!map.getProjection()) {
        timeoutId = setTimeout(build, 100);
        return;
      }

      const icon: google.maps.Symbol = {
        path: g.SymbolPath.CIRCLE,
        fillColor,
        fillOpacity: 1,
        strokeOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 0.75,
        scale: 4,
      };

      let index = 0;
      const markers: google.maps.Marker[] = [];

      const runChunk = () => {
        if (cancelled) return;
        const end = Math.min(index + MARKER_BUILD_CHUNK, points.length);
        for (; index < end; index++) {
          const p = points[index];
          markers.push(
            new g.Marker({
              position: { lat: p.lat, lng: p.lng },
              icon,
            })
          );
        }
        if (index < points.length) {
          rafRef.current = requestAnimationFrame(runChunk);
          return;
        }
        if (cancelled) {
          markers.forEach((m) => m.setMap(null));
          return;
        }
        markersRef.current = markers;
        clustererRef.current = new MarkerClusterer({
          map,
          markers,
          algorithm: new SuperClusterViewportAlgorithm({
            radius: 92,
            maxZoom: 16,
            viewportPadding: 88,
          }),
          renderer: clusterRenderer,
        });
      };

      runChunk();
    };

    build();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      cleanup();
    };
  }, [map, points, fillColor, visible, clusterRenderer]);

  return null;
};

const formatDateForInput = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getCurrentDate = () => formatDateForInput(new Date());
const getMinSelectableDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return formatDateForInput(d);
};

const normalizeStatusFilterFlags = (status: string) => {
  const s = String(status || "PENDING").toUpperCase();
  return {
    filter_by_created_at: s === "PENDING",
    filter_by_delivered_at: s === "DELIVERED",
    filter_by_rejected_at: false,
    filter_by_cancelled_at: s === "CANCELLED",
  };
};

const clampDateToRange = (value: string, min: string, max: string): string => {
  if (!value) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

const DeliveryHeatmap: React.FC = () => {
  const { t } = useTranslation();
  const today = getCurrentDate();
  const minSelectableDate = getMinSelectableDate();

  const [loading, setLoading] = useState(false);
  const [allPoints, setAllPoints] = useState<DeliveryPoint[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({
    DELIVERED: 0,
    CANCELLED: 0,
    PENDING: 0,
  });

  const [open, setOpen] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [globalFilters, setGlobalFilters] = useState<any>(null);

  const [selectedRegion, setRegion] = useState<any>([]);
  const [selectedSalesOffice, setSelectedSalesOffice] = useState<any>([]);
  const [channelId, setChannelId] = useState<any>([]);
  /** "ALL" = every status on map; else API + map filter to one status (default: pending) */
  const [deliveryStatus, setDeliveryStatus] = useState<string>("PENDING");
  const [startDate, setStartDate] = useState<any>(today);
  const [endDate, setEndDate] = useState<any>(today);
  const [timeFrame, setSelectTimeFrame] = useState<any>("Today");
  /** false = heatmap, true = individual delivery points */
  const [showIndividualPoints, setShowIndividualPoints] = useState(false);

  const getGlobalFilters = async (data: any) => {
    try {
      setLoadingFilter(true);
      const response = await getGlobalFilter(data);
      setLoadingFilter(false);
      setGlobalFilters(response);
    } catch (error) {
      console.error("Error fetching global filters:", error);
      setLoadingFilter(false);
    }
  };

  const buildFilterPayload = () => {
    const data: any = {};
    if (selectedRegion.length > 0) data["regions"] = selectedRegion;
    if (selectedSalesOffice.length > 0) data["sales_office_ids"] = selectedSalesOffice;
    if (channelId.length > 0) data["channel_ids"] = channelId;
    if (deliveryStatus) data["status"] = deliveryStatus;
    Object.assign(data, normalizeStatusFilterFlags(deliveryStatus));
    data["endpoint"] = "visit-summary";
    data["start_date"] = formatCurrentStartDate(new Date(startDate));
    data["end_date"] = formatCurrentEndDate(new Date(endDate));
    data["positions"] = [delivery_man];
    return data;
  };

  const handleFiltersUpdate = () => {
    getGlobalFilters(buildFilterPayload());
  };

  useEffect(() => {
    getGlobalFilters(buildFilterPayload());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedSalesOffice([]);
    setChannelId([]);
  }, [selectedRegion]);

  useEffect(() => {
    setChannelId([]);
  }, [selectedSalesOffice]);

  const fetchData = async (statusFilter?: string) => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const payload: any = {
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate + "T23:59:59").toISOString(),
      };
      const activeStatus = statusFilter || deliveryStatus || "PENDING";
      payload.status = activeStatus;
      Object.assign(payload, normalizeStatusFilterFlags(activeStatus));
      if (selectedRegion.length > 0) payload.regions = selectedRegion;
      if (selectedSalesOffice.length > 0) payload.sales_office_ids = selectedSalesOffice;
      if (channelId.length > 0) payload.channel_ids = channelId;

      const res: any = await getDeliveryCoordinates(payload);
      const raw = res?.data?.data || res?.data || {};
      const deliveries: any[] = Array.isArray(raw) ? raw : (raw.items || []);

      const points: DeliveryPoint[] = [];
      const counts: Record<string, number> = { DELIVERED: 0, CANCELLED: 0, PENDING: 0 };
      let skippedNoCoords = 0;

      deliveries.forEach((delivery: any) => {
        const latVal = delivery.delivered_latitude ?? delivery.latitude ?? delivery.lat;
        const lngVal = delivery.delivered_longitude ?? delivery.longitude ?? delivery.lng;
        const status = (delivery.status || "PENDING").toUpperCase();

        if (latVal == null || lngVal == null || latVal === "" || lngVal === "") {
          skippedNoCoords++;
          if (counts[status] !== undefined) counts[status]++;
          return;
        }

        const lat = parseFloat(latVal);
        const lng = parseFloat(lngVal);

        if (!isNaN(lat) && !isNaN(lng)) {
          points.push({ lat, lng, status });
          if (counts[status] !== undefined) counts[status]++;
        }
      });

      setAllPoints(points);
      setStatusCounts(counts);
      setTotalCount(deliveries.length);
      setSkippedCount(skippedNoCoords);
    } catch (err) {
      console.error("Failed to fetch delivery coordinates:", err);
      setAllPoints([]);
      setTotalCount(0);
      setSkippedCount(0);
      setStatusCounts({ DELIVERED: 0, CANCELLED: 0, PENDING: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(deliveryStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    fetchData(deliveryStatus);
    setOpen(false);
  };

  const handleClearFilters = () => {
    setRegion([]);
    setSelectedSalesOffice([]);
    setChannelId([]);
    setDeliveryStatus("PENDING");
    setStartDate(getCurrentDate());
    setEndDate(getCurrentDate());
    setSelectTimeFrame("Today");
    setOpen(false);
    fetchData("PENDING");
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextStart = clampDateToRange(e.target.value, minSelectableDate, today);
    setStartDate(nextStart);
    if (endDate && endDate < nextStart) {
      setEndDate(nextStart);
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentStart = clampDateToRange(String(startDate), minSelectableDate, today);
    const nextEnd = clampDateToRange(e.target.value, currentStart, today);
    setEndDate(nextEnd);
  };

  const displayedStatuses = useMemo((): Array<keyof typeof STATUS_COLORS> => {
    const s = deliveryStatus;
    if (s === "DELIVERED" || s === "CANCELLED" || s === "PENDING") return [s];
    return ["PENDING"];
  }, [deliveryStatus]);

  const pointsByStatus = useMemo(() => {
    const result: Record<string, DeliveryPoint[]> = {};
    for (const status of STATUSES) {
      const raw = allPoints.filter((p) => p.status === status);
      result[status] = spreadOverlappingCoordinates(raw);
    }
    return result;
  }, [allPoints]);

  const clusteredByStatus = useMemo(() => {
    const result: Record<string, WeightedPoint[]> = {};
    for (const status of STATUSES) {
      const raw = allPoints.filter((p) => p.status === status);
      result[status] = clusterPoints(raw);
    }
    return result;
  }, [allPoints]);

  const hasPointsOnMap = allPoints.length > 0;

  useEffect(() => {
    if (!hasPointsOnMap && showIndividualPoints) {
      setShowIndividualPoints(false);
    }
  }, [hasPointsOnMap, showIndividualPoints]);

  return (
    <DashboardPage>
      <div className="flex flex-col h-full w-full p-4 gap-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-xl font-semibold text-gray-800">Delivery Heatmap</h1>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-13 font-normal rounded transition-all"
            >
              <img src={Filter} alt="Filter" className="w-4 h-4 mr-2" />
              {t("planogram.filter")}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-lg px-4 py-2.5 shadow-sm flex-wrap">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-700">
            {STATUSES.map((status) => (
              <span key={status} className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: STATUS_COLORS[status].dot }}
                />
                <span className="text-gray-600">{STATUS_COLORS[status].label}:</span>
                <span className="font-semibold text-gray-900 tabular-nums">
                  {statusCounts[status] ?? 0}
                </span>
              </span>
            ))}
            <span className="text-gray-300 hidden sm:inline">|</span>
            <span>
              <span className="text-gray-600">Total:</span>{" "}
              <span className="font-semibold text-gray-900 tabular-nums">{totalCount}</span>
            </span>
          </div>
          <div className="ml-auto flex items-center gap-4 text-sm text-gray-500">
            <span>
              On map: <span className="font-semibold text-gray-700">{allPoints.length}</span>
            </span>
            {skippedCount > 0 && (
              <span className="text-amber-600">
                No coordinates: <span className="font-semibold">{skippedCount}</span>
              </span>
            )}
            <div
              className="flex items-center gap-2 pl-2 border-l border-gray-200"
              title={
                hasPointsOnMap
                  ? "Green = delivered, yellow = pending, red = cancelled."
                  : "Points view needs at least one delivery with coordinates. Adjust filters or date range."
              }
            >
              <span className="text-sm text-gray-600">Heatmap</span>
              <button
                type="button"
                role="switch"
                aria-checked={showIndividualPoints}
                aria-disabled={!hasPointsOnMap || loading}
                disabled={!hasPointsOnMap || loading}
                aria-label="Toggle between heatmap and individual delivery points"
                onClick={() => {
                  if (!hasPointsOnMap || loading) return;
                  setShowIndividualPoints((v) => !v);
                }}
                className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full p-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-purple focus:ring-offset-2 ${
                  showIndividualPoints ? "bg-purple" : "bg-gray-300"
                } ${!hasPointsOnMap || loading ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <span
                  className={`pointer-events-none h-7 w-7 rounded-full bg-white shadow transition-[margin] duration-200 ${
                    showIndividualPoints ? "ml-auto" : ""
                  }`}
                />
              </button>
              <span className="text-sm text-gray-700 font-medium">Points</span>
            </div>
          </div>
        </div>

        <div className="flex-1 relative rounded-lg overflow-hidden border border-gray-200 shadow-sm" style={{ minHeight: "500px" }}>
          {loading && (
            <div className="absolute inset-0 z-10 bg-white/60 flex items-center justify-center">
              <Loader />
            </div>
          )}
          <APIProvider
            apiKey={process.env.REACT_APP_GOOGLE_MAP_KEY || ""}
            libraries={["visualization"]}
          >
            <Map
              style={{ width: "100%", height: "100%" }}
              defaultCenter={saudiCenter}
              defaultZoom={7}
              gestureHandling="greedy"
              disableDefaultUI={true}
              zoomControl={true}
              minZoom={6}
              maxZoom={18}
              restriction={{
                latLngBounds: {
                  north: 32.5,
                  south: 16.0,
                  east: 56.0,
                  west: 34.0,
                },
                strictBounds: true,
              }}
            >
              {showIndividualPoints && hasPointsOnMap
                ? displayedStatuses.map((status) => (
                    <DeliveryMarkersLayer
                      key={`pts-${status}`}
                      statusKey={status}
                      points={pointsByStatus[status] || []}
                      visible
                    />
                  ))
                : displayedStatuses.map((status) => (
                    <StatusHeatmapLayer
                      key={`hm-${status}`}
                      clustered={clusteredByStatus[status] || []}
                      gradient={STATUS_COLORS[status].gradient}
                      visible
                    />
                  ))}
            </Map>
          </APIProvider>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end sm:items-start items-center z-50">
          <div className="relative bg-white shadow-lg w-full max-w-md h-full">
            <div className="absolute z-30 top-[50%] left-[45%]">
              {loadingFilter && <Loader />}
            </div>
            <div className="flex justify-between p-6 border-b-2">
              <div className="text-16 font-bold">
                {t("visitreports.filters.title")}
              </div>
              <div>
                <img
                  className="cursor-pointer"
                  onClick={() => setOpen(false)}
                  src={Close}
                  alt=""
                />
              </div>
            </div>
            <div className="flex flex-col justify-between h-[calc(100vh-100px)] px-12 pt-6 overflow-y-auto scrollbar-thin">
              <div className="flex flex-col gap-[30px]">
                <div className="flex space-x-4">
                  <div className="w-1/2">
                    <DropdownMulti
                      label={t("visitreports.filters.region")}
                      options={globalFilters?.regions?.map((r: any) => ({
                        value: r,
                        label: r,
                      })) || []}
                      selectedValues={selectedRegion}
                      onClose={handleFiltersUpdate}
                      onChange={setRegion}
                      placeholder={t("visitreports.filters.selectRegion")}
                      showSelectAll={true}
                    />
                  </div>
                  <div className="w-1/2">
                    <DropdownMulti
                      label={t("visitreports.filters.salesOffice")}
                      options={
                        globalFilters?.sales_offices?.map((s: any) => ({
                          value: s.id,
                          label: s.name,
                        })) || []
                      }
                      selectedValues={selectedSalesOffice}
                      onClose={handleFiltersUpdate}
                      onChange={setSelectedSalesOffice}
                      disabled={loadingFilter}
                      placeholder={t("visitreports.filters.selectSalesOffice")}
                      showSelectAll={true}
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-full">
                    <DropdownMulti
                      label={t("visitreports.filters.channelID")}
                      options={
                        globalFilters?.channels?.map((c: any) => ({
                          value: c.id,
                          label: c.name,
                        })) || []
                      }
                      selectedValues={channelId}
                      onClose={handleFiltersUpdate}
                      onChange={setChannelId}
                      disabled={loadingFilter}
                      placeholder={t("visitreports.filters.selectChannelID")}
                    />
                  </div>
                </div>

                <div className="w-full">
                  <Dropdown
                    label={t("visitreports.filters.deliverystatus")}
                    options={[
                      { value: "DELIVERED", label: "Delivered" },
                      { value: "CANCELLED", label: "Cancelled" },
                      { value: "PENDING", label: "Pending" },
                    ]}
                    selectedValue={deliveryStatus}
                    onChange={(v) => setDeliveryStatus(String(v))}
                    placeholder={t("visitreports.filters.deliverystatus")}
                  />
                </div>

                <div className="date-range-picker space-y-2 p-1  rounded-lg ">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-14 font-semibold text-gray-700">
                      {t("visitreports.filters.date")}:
                    </label>
                  </div>
                  <div className="flex space-x-4 justify-between w-full">
                    <div className="w-full">
                      <label className="block text-10 text-gray-700 mb-1">
                        {t("visitreports.filters.startDate")}:
                      </label>
                      <input
                        type="date"
                        className="rounded p-2 border-2 w-full border-gray-400"
                        value={typeof startDate === "string" ? startDate : formatDateForInput(new Date(startDate))}
                        onChange={handleStartDateChange}
                        min={minSelectableDate}
                        max={today}
                        onKeyDown={(e) => e.preventDefault()}
                      />
                    </div>
                    <div className="w-full">
                      <label className="block text-10 text-gray-700 mb-1">
                        {t("visitreports.filters.endDate")}:
                      </label>
                      <input
                        type="date"
                        className="rounded p-2 border-2 w-full border-gray-400"
                        value={typeof endDate === "string" ? endDate : formatDateForInput(new Date(endDate))}
                        onChange={handleEndDateChange}
                        min={typeof startDate === "string" ? startDate : formatDateForInput(new Date(startDate))}
                        max={today}
                        onKeyDown={(e) => e.preventDefault()}
                      />
                    </div>
                  </div>
                  <div>
                    <TimeFrameSelector
                      customClass="w-full border-2 rounded p-2 border-gray-300"
                      setSelectTimeFrame={setSelectTimeFrame}
                      timeFrame={timeFrame}
                      setStartDate={setStartDate}
                      setEndDate={setEndDate}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between mt-4 pb-6">
                <Button
                  onClick={handleClearFilters}
                  label={t("visitreports.filters.clearFilter")}
                  type="grey"
                />
                <Button
                  onClick={handleApply}
                  label={t("visitreports.filters.applyFilter")}
                  type="purple"
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardPage>
  );
};

export default DeliveryHeatmap;